import React from 'react'
import { useAuth, UserPermissions, UserRole, useUser } from '@modmd/data'
import SideNav from 'internal-portal/routes/components/SideNav'
import { ROUTES } from 'internal-portal/constants/routes'
import Dashboard, { DashboardProps } from 'components/templates/Dashboard'
import { Box } from 'components/Layout'
import Icon from 'components/Icon'
import { UpdateExpiredPassword } from 'utils/UpdateExpiredPassword'

type DashboardPageProps = Partial<DashboardProps>

const DashboardPage = ({ Header = null, Sidebar, children }: DashboardPageProps) => {
  const [updatedPasswordDate, setUpdatedPasswordDate] = React.useState<Date | null>(null)
  const { hasPermission, logout, hasRoles } = useAuth()

  const auth = useAuth()

  const { data, refetch } = useUser({ userId: auth.data.User.id })
  const isPatient = hasRoles([UserRole.PATIENT])

  React.useEffect(() => {
    if (data?.user?.updatedPasswordAt) {
      setUpdatedPasswordDate(data?.user?.updatedPasswordAt)
    }
  }, [data?.user?.updatedPasswordAt])

  const updateDate = React.useCallback(async () => {
    setUpdatedPasswordDate(new Date())
    await refetch()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <>
      {auth.data.User && !isPatient ? (
        <UpdateExpiredPassword
          onLogOut={() => logout()}
          updatedPasswordAt={updatedPasswordDate}
          updatePasswordDate={updateDate}
        />
      ) : null}
      <Dashboard
        Header={Header}
        Sidebar={
          Sidebar || (
            <SideNav>
              <SideNav.Item route={`/${ROUTES.DASHBOARD}`} label="Dashboard" icon={Icon.Home} />
              {hasPermission([
                UserPermissions.SEE_FULL_TEST_SESSIONS,
                UserPermissions.SEE_GROUP_COMPANY_TEST_SESSIONS,
                UserPermissions.SCHEDULE_TEST_SESSION_ALL_USERS,
                UserPermissions.SEE_FINISHED_TEST_SESSIONS,
                UserPermissions.SEE_FINISHED_TEST_SESSIONS_GROUP_ASSIGNED,
                UserPermissions.SEE_STAFFED_TEST_SESSION,
              ]) && (
                <SideNav.Item
                  route={`/${ROUTES.SESSIONS}`}
                  label="List Of Sessions"
                  icon={Icon.Heartbeat}
                />
              )}
              {hasPermission([
                UserPermissions.SEE_FULL_LIST_TESTS,
                UserPermissions.SEE_FULL_COMPLETED_TESTS,
                UserPermissions.SEE_GROUP_COMPANY_LIST_TESTS,
              ]) && (
                <SideNav.Item route={`/${ROUTES.TESTS}`} label="List Of Tests" icon={Icon.Test} />
              )}
              {hasPermission([
                UserPermissions.SEE_FULL_LIST_TESTS,
                UserPermissions.SEE_GROUP_COMPANY_LIST_TESTS,
              ]) && (
                <Box ml="1.5rem" display="grid" gridGap=".25rem">
                  <SideNav.Item
                    route={`/${ROUTES.UNVERIFIED_TESTS}`}
                    label="Unverified Tests"
                    icon={Icon.Test}
                  />
                </Box>
              )}
              {hasPermission([UserPermissions.SEE_EVENTS, UserPermissions.SCHEDULE_EVENT]) && (
                <SideNav.Item
                  route={`/${ROUTES.EVENTS}`}
                  label="List Of Events"
                  icon={Icon.Calendar}
                />
              )}
              {hasPermission([UserPermissions.SEE_EVENT_ATTENDEE_LIST]) && (
                <SideNav.Item
                  route={`/${ROUTES.EVENT_ATTENDEES}`}
                  label="List Of Event Attendees"
                  icon={Icon.Test}
                />
              )}
              {hasPermission([UserPermissions.SEE_VACCINE_RECORD_VERIFICATION]) && (
                <SideNav.Item
                  route={`/${ROUTES.VACCINE_RECORD_VERIFICATION}`}
                  label="Vaccine Record Verification"
                  icon={Icon.CheckmarkSquare}
                />
              )}
              {hasPermission([UserPermissions.SEE_INSURANCE_REVIEW_VERIFICATION]) && (
                <SideNav.Item
                  route={`/${ROUTES.INSURANCE_REVIEW_VERIFICATION}`}
                  label="Insurance Review Verification"
                  icon={Icon.CheckmarkSquare}
                />
              )}
              {hasPermission([UserPermissions.SEE_EDIT_INSURANCE_COMPANIES]) && (
                <Box ml="1.5rem" display="grid" gridGap=".25rem">
                  <SideNav.Item
                    route={`/${ROUTES.INSURANCE_COMPANIES}`}
                    label="Insurance Companies"
                    icon={Icon.SettingsOutline}
                  />
                </Box>
              )}
              {hasPermission([UserPermissions.SEE_EDIT_INSURANCE_COMPANIES]) && (
                <Box ml="1.5rem" display="grid" gridGap=".25rem">
                  <SideNav.Item
                    route={`/${ROUTES.INSURANCE_ERROR_LOGS}`}
                    label="Insurance Error Logs"
                    icon={Icon.TextDocumentInverted}
                  />
                </Box>
              )}
              {hasPermission([UserPermissions.SEE_LIST_COMPANIES]) && (
                <SideNav.Item
                  route={`/${ROUTES.COMPANIES}`}
                  label="List Of Companies"
                  icon={Icon.List}
                />
              )}
              {hasPermission([UserPermissions.SEE_ANONAMYZED_ANALYTICS]) && (
                <SideNav.Item
                  route={`/${ROUTES.ANALYITCS}`}
                  label="Analytics"
                  icon={Icon.ClipboardOutline}
                />
              )}
              {hasPermission([
                UserPermissions.SEE_ANALYTICS_TEST_RESULTS,
                UserPermissions.SEE_ANALYTICS_APP_USAGE,
                UserPermissions.SEE_ANALYTICS_OPTIN_MARKETING,
              ]) && (
                <Box ml="1.5rem" display="grid" gridGap=".25rem">
                  {hasPermission([UserPermissions.SEE_ANALYTICS_TEST_RESULTS]) && (
                    <SideNav.Item
                      route={`/${ROUTES.ANALYTICS_TESTS_RESULTS}`}
                      label="Tests and Results"
                      icon={Icon.Test}
                    />
                  )}
                  {hasPermission([UserPermissions.SEE_ANALYTICS_TEST_RESULTS]) && (
                    <SideNav.Item
                      route={`/${ROUTES.ANALYTICS_INSURANCE_TESTS}`}
                      label="Insurance Tests and Results"
                      icon={Icon.Test}
                    />
                  )}
                  {hasPermission([UserPermissions.SEE_ANALYTICS_APP_USAGE]) && (
                    <SideNav.Item
                      route={`/${ROUTES.ANALYTICS_APP_USAGE}`}
                      label="App usage"
                      icon={Icon.PieChartOutline}
                    />
                  )}
                  {hasPermission([UserPermissions.SEE_ANALYTICS_OPTIN_MARKETING]) && (
                    <SideNav.Item
                      route={`/${ROUTES.ANALYTICS_MARKETING}`}
                      label="Opt In Marketing"
                      icon={Icon.EmailOutline}
                    />
                  )}
                </Box>
              )}
              {hasPermission([UserPermissions.SEE_ALL_USERS]) && (
                <SideNav.Item
                  route={`/${ROUTES.USERS_ALL}`}
                  label="All users"
                  icon={Icon.Thermometer}
                />
              )}
              {hasPermission([
                UserPermissions.SEE_PATIENTS,
                UserPermissions.SEE_SUBPERSONS,
                UserPermissions.SEE_COMPANY_OWNERS,
                UserPermissions.SEE_STAFF,
              ]) && (
                <Box ml="1.5rem" display="grid" gridGap=".25rem">
                  {hasPermission([UserPermissions.SEE_PATIENTS]) && (
                    <SideNav.Item
                      route={`/${ROUTES.USERS_PATIENT}`}
                      label="Patients"
                      icon={Icon.Thermometer}
                    />
                  )}
                  {hasPermission([UserPermissions.SEE_SUBPERSONS]) && (
                    <SideNav.Item
                      route={`/${ROUTES.USERS_SUBPERSONS}`}
                      label="Subpersons"
                      icon={Icon.AddPerson}
                    />
                  )}
                  {hasPermission([UserPermissions.SEE_COMPANY_OWNERS]) && (
                    <SideNav.Item
                      route={`/${ROUTES.USERS_COMPANY_OWNER}`}
                      label="Company owners"
                      icon={Icon.Briefcase}
                    />
                  )}
                  {hasPermission([UserPermissions.SEE_STAFF]) && (
                    <SideNav.Item
                      route={`/${ROUTES.USERS_STAFF}`}
                      label="Staff"
                      icon={Icon.Person}
                    />
                  )}
                </Box>
              )}
              {hasPermission([UserPermissions.SEE_REPORTS_LOGS]) && (
                <SideNav.Item
                  route={`/${ROUTES.REPORTS_LOGS}`}
                  label="Lab interface logs"
                  icon={Icon.TextDocumentInverted}
                />
              )}
              {/* <SideNav.Item
              route={`/${ROUTES.DRIVE_THROUGH_LOCATIONS}`}
              label="Drive-through Locations"
              icon={Icon.Location}
            /> */}
              {hasPermission([UserPermissions.SEE_USER_LOGS]) && (
                <SideNav.Item
                  route={`/${ROUTES.USER_LOGS}`}
                  label="User Logs"
                  icon={Icon.FileTextOutline}
                />
              )}
              {hasPermission([UserPermissions.SEE_FINANCES]) && (
                <SideNav.Item
                  route={`/${ROUTES.FINANCES}`}
                  label="Finance"
                  icon={Icon.ShoppingCartOutline}
                />
              )}
              {hasPermission([UserPermissions.SEE_FINANCES]) && (
                <Box ml="1.5rem" display="grid" gridGap=".25rem">
                  <SideNav.Item
                    route={`/${ROUTES.FINANCES_PRICES}`}
                    label="Prices"
                    icon={Icon.CreditCardOutline}
                  />
                </Box>
              )}
              {hasPermission([UserPermissions.ADD_LOCATION]) && (
                <Box ml="1.5rem" display="grid" gridGap=".25rem">
                  <SideNav.Item
                    route={`/${ROUTES.FINANCES_LOCATIONS}`}
                    label="Locations"
                    icon={Icon.Location}
                  />
                </Box>
              )}
              {hasPermission([UserPermissions.SEE_USER_PROFILE]) && (
                <SideNav.Item
                  route={`/${ROUTES.USER_PROFILE}`}
                  label="User Profile"
                  icon={Icon.Person}
                />
              )}
            </SideNav>
          )
        }
      >
        <Box p="1rem">{children}</Box>
      </Dashboard>
    </>
  )
}

export default DashboardPage
